/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo } from 'react';
import { Spin, message, Button, Select, DatePicker } from 'antd';
import { DownloadOutlined, ReloadOutlined } from '@ant-design/icons';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
    Tooltip, Legend, ResponsiveContainer, Cell, LabelList } from 'recharts';
import FleetService from '../../../services/FleetService';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import moment from 'moment';

const NAVY  = '#1a3a5c';
const toN   = v => Number(v || 0);

function KPI({ label, value, color=NAVY, sub, pct }) {
    return (
        <div style={{ background:'#fff', border:'1px solid #e8e8e8', borderRadius:8,
            padding:'14px 18px', borderTop:`3px solid ${color}`, flex:1 }}>
            <div style={{ fontSize:11, color:'#888', marginBottom:4 }}>{label}</div>
            <div style={{ fontSize:22, fontWeight:700, color }}>{value}</div>
            {sub && <div style={{ fontSize:11, color:'#aaa', marginTop:2 }}>{sub}</div>}
            {pct != null && (
                <div style={{ marginTop:6, background:'#f0f0f0', borderRadius:4, height:5 }}>
                    <div style={{ width:`${Math.min(100,pct)}%`, background:color, height:5, borderRadius:4 }}/>
                </div>
            )}
        </div>
    );
}
function ChartCard({ title, children, height=280 }) {
    return (
        <div style={{ background:'#fff', border:'1px solid #e8e8e8', borderRadius:8,
            padding:'14px 16px', marginBottom:14 }}>
            <div style={{ fontWeight:600, fontSize:13, color:NAVY, marginBottom:10 }}>{title}</div>
            <div style={{ height }}>{children}</div>
        </div>
    );
}

const utilColor = v => v >= 70 ? '#52c41a' : v >= 40 ? '#faad14' : '#f5222d';

export default function FleetAnalyticsUtilisation() {
    const [trips,    setTrips]    = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [loading,  setLoading]  = useState(true);
    const [region,     setRegion]    = useState('');
    const [programme,  setProgramme] = useState('');
    const [facility,   setFacility]  = useState('');
    const [vehType,    setVehType]   = useState('');
    const [dateFrom,   setDateFrom]  = useState('');
    const [dateTo,     setDateTo]    = useState('');

    const load = async () => {
        setLoading(true);
        try {
            const [r1, r2, r3] = await Promise.all([
                FleetService.getAllTripLogs(),
                FleetService.getVehiclesList(),
                FleetService.getDeployments(),
            ]);
            if (Number(r1?.data?.code) === 200) setTrips(r1.data.result?.data || r1.data.result || []);
            if (Number(r2?.data?.code) === 200) setVehicles(r2.data.result || []);
        } catch (e) { message.error('Failed to load data'); }
        setLoading(false);
    };

    useEffect(() => { load(); }, []);

    const regions = useMemo(() => [...new Set(vehicles.map(v => v.region).filter(Boolean))], [vehicles]);

    const filteredVehicles = useMemo(() =>
        region ? vehicles.filter(v => v.region === region) : vehicles
    , [vehicles, region]);

    // Trip count + km per vehicle
    const vehicleUtil = useMemo(() => {
        return filteredVehicles.map(v => {
            const vTrips  = trips.filter(t => String(t.vehicle_id) === String(v.id));
            const totalKm = vTrips.reduce((s,t) => s + (toN(t.odometer_end) - toN(t.odometer_start)), 0);
            // Days active = unique trip dates
            const days    = new Set(vTrips.map(t => t.trip_date)).size;
            // Utilisation: days used / 30 (monthly estimate)
            const util    = Math.min(100, Math.round(days / 30 * 100));
            return {
                id:    v.id, reg: v.registration_no, type: v.vehicle_type,
                region: v.region, programme: v.assigned_programme,
                tripCount: vTrips.length, totalKm: Math.round(totalKm),
                activeDays: days, util,
            };
        }).sort((a,b) => b.util - a.util);
    }, [filteredVehicles, trips]);

    // Monthly activity
    const monthlyActivity = useMemo(() => {
        const m = {};
        trips.forEach(t => {
            const mo = t.trip_date ? moment(t.trip_date).format('MMM YY') : 'Unknown';
            if (!m[mo]) m[mo] = { month:mo, trips:0, km:0, vehicles:new Set() };
            m[mo].trips++;
            m[mo].km += toN(t.odometer_end) - toN(t.odometer_start);
            m[mo].vehicles.add(t.vehicle_id);
        });
        return Object.values(m).sort((a,b) => {
            const da = moment(a.month, 'MMM YY'), db = moment(b.month, 'MMM YY');
            return da - db;
        }).slice(-12).map(m => ({ ...m, vehicles: m.vehicles.size }));
    }, [trips]);

    // Idle vehicles (no trips in last 30 days)
    const idleVehicles = useMemo(() => {
        const cutoff = moment().subtract(30, 'days');
        const recentVehicleIds = new Set(
            trips.filter(t => t.trip_date && moment(t.trip_date).isAfter(cutoff))
                 .map(t => String(t.vehicle_id))
        );
        return filteredVehicles.filter(v => !recentVehicleIds.has(String(v.id)));
    }, [filteredVehicles, trips]);

    // Utilisation heat — monthly trip count per vehicle (heatmap-style bar)
    const util30     = vehicleUtil.filter(v => v.util >= 70).length;
    const utilLow    = vehicleUtil.filter(v => v.util < 40).length;
    const avgUtil    = vehicleUtil.length ? Math.round(vehicleUtil.reduce((s,v)=>s+v.util,0)/vehicleUtil.length) : 0;
    const totalTrips = trips.length;
    const totalKm    = trips.reduce((s,t) => s + (toN(t.odometer_end) - toN(t.odometer_start)), 0);

    const exportXlsx = () => {
        const ws = XLSX.utils.json_to_sheet(vehicleUtil.map(v => ({
            'Reg No':     v.reg,
            'Type':       v.type,
            'Region':     v.region,
            'Programme':  v.programme,
            'Trips':      v.tripCount,
            'Total KM':   v.totalKm,
            'Active Days':v.activeDays,
            'Utilisation %': v.util,
        })));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Utilisation');
        saveAs(new Blob([XLSX.write(wb, { bookType:'xlsx', type:'array' })]),
            `Fleet_Utilisation_${new Date().toISOString().slice(0,10)}.xlsx`);
    };

    return (
        <div className="page-content" style={{ padding:'20px 24px' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:20 }}>
                <div>
                    <h4 style={{ margin:0, fontWeight:700, color:NAVY }}>Fleet Utilisation Heatmap</h4>
                    <div style={{ fontSize:13, color:'#888', marginTop:2 }}>
                        Vehicle activity rates · idle fleet identification · monthly trip trends
                    </div>
                </div>
                <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                    <Select allowClear placeholder="All Regions" style={{ width:130 }} size="small"
                        value={region||undefined} onChange={v=>setRegion(v||'')}>
                        {regions.map(r => <Select.Option key={r} value={r}>{r}</Select.Option>)}
                    </Select>
                    <Button icon={<ReloadOutlined />} onClick={load}/>
                    <Button type="primary" icon={<DownloadOutlined />} onClick={exportXlsx} disabled={!vehicleUtil.length}>Export</Button>
                </div>
            </div>

            <Spin spinning={loading}>
                {/* KPIs */}
                <div style={{ display:'flex', gap:12, marginBottom:20, flexWrap:'wrap' }}>
                    <KPI label="Vehicles Tracked"  value={vehicleUtil.length}  color={NAVY}/>
                    <KPI label="Fleet Avg Utilisation" value={`${avgUtil}%`}   color={utilColor(avgUtil)} pct={avgUtil}/>
                    <KPI label="High Utilisation (≥70%)" value={util30}        color="#52c41a" sub="active fleet"/>
                    <KPI label="Low Utilisation (&lt;40%)" value={utilLow}     color="#f5222d" sub="underused vehicles"/>
                    <KPI label="Idle (30 days)"    value={idleVehicles.length} color={idleVehicles.length>0?'#fa8c16':'#52c41a'} sub="no trips recorded"/>
                    <KPI label="Total Trips"       value={totalTrips.toLocaleString()} color="#2e86c1"/>
                    <KPI label="Total KM"          value={`${totalKm.toLocaleString()} km`} color="#722ed1"/>
                </div>

                {/* Per-vehicle utilisation */}
                <ChartCard title="Vehicle Utilisation % (sorted highest → lowest)" height={300}>
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={vehicleUtil.slice(0,20).map(v=>({ name:v.reg||'—', util:v.util, km:v.totalKm }))}
                            layout="vertical" margin={{ top:0, right:60, left:85, bottom:0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false}/>
                            <XAxis type="number" domain={[0,100]} tick={{ fontSize:10 }} tickFormatter={v=>`${v}%`}/>
                            <YAxis type="category" dataKey="name" tick={{ fontSize:10 }} width={80}/>
                            <Tooltip formatter={(v,n)=>[n==='util'?`${v}%`:`${toN(v).toLocaleString()} km`, n==='util'?'Utilisation':'Total KM']}/>
                            <Legend wrapperStyle={{ fontSize:10 }}/>
                            <Bar dataKey="util" name="Utilisation" radius={[0,4,4,0]}>
                                {vehicleUtil.slice(0,20).map((v,i) => <Cell key={i} fill={utilColor(v.util)}/>)}
                                <LabelList dataKey="util" position="right" style={{ fontSize:9 }} formatter={v=>`${v}%`}/>
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </ChartCard>

                {/* Monthly trend */}
                <div style={{ display:'grid', gridTemplateColumns:'1.5fr 1fr', gap:14 }}>
                    <ChartCard title="Monthly Trip Activity (last 12 months)" height={240}>
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={monthlyActivity} margin={{ top:5, right:20, left:5, bottom:5 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0"/>
                                <XAxis dataKey="month" tick={{ fontSize:10 }}/>
                                <YAxis yAxisId="t" tick={{ fontSize:10 }}/>
                                <YAxis yAxisId="v" orientation="right" tick={{ fontSize:10 }}/>
                                <Tooltip/>
                                <Legend wrapperStyle={{ fontSize:10 }}/>
                                <Line yAxisId="t" type="monotone" dataKey="trips"    name="Trips"    stroke={NAVY}     strokeWidth={2} dot={{ r:3 }}/>
                                <Line yAxisId="v" type="monotone" dataKey="vehicles" name="Vehicles" stroke="#52c41a" strokeWidth={2} dot={{ r:3 }} strokeDasharray="5 3"/>
                            </LineChart>
                        </ResponsiveContainer>
                    </ChartCard>

                    {/* Idle vehicles */}
                    <ChartCard title={`Idle Vehicles (no trips in 30 days) — ${idleVehicles.length}`} height={240}>
                        {idleVehicles.length === 0 ? (
                            <div style={{ display:'flex', alignItems:'center', justifyContent:'center',
                                height:'100%', color:'#52c41a', fontWeight:600, fontSize:14 }}>
                                ✓ All vehicles have recent activity
                            </div>
                        ) : (
                            <div style={{ overflowY:'auto', height:'100%' }}>
                                {idleVehicles.map((v,i) => (
                                    <div key={i} style={{ display:'flex', justifyContent:'space-between',
                                        padding:'8px 12px', borderBottom:'1px solid #f0f0f0',
                                        background: i%2===0 ? '#fff' : '#fafafa', fontSize:12 }}>
                                        <div>
                                            <strong>{v.registration_no}</strong>
                                            <span style={{ color:'#888', marginLeft:8 }}>{v.vehicle_type}</span>
                                        </div>
                                        <div style={{ color:'#888', fontSize:11 }}>{v.region||'—'}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </ChartCard>
                </div>

                {!loading && vehicleUtil.length === 0 && (
                    <div style={{ textAlign:'center', padding:60, color:'#aaa',
                        border:'2px dashed #e8e8e8', borderRadius:8 }}>
                        No trip log data. Log trips to see utilisation analytics.
                    </div>
                )}
            </Spin>
        </div>
    );
}